<!--
  Copyright (C) 2022 - Jeci SARL - https://jeci.fr

  This program is free software: you can redistribute it and/or modify
  it under the terms of the GNU Affero General Public License as
  published by the Free Software Foundation, either version 3 of the
  License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful,
  but WITHOUT ANY WARRANTY; without even the implied warranty of
  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
  GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License
  along with this program.  If not, see https://www.gnu.org/licenses/agpl-3.0.html.
-->

<template>
  <ActionMenu ref="fileMenuAction" :actionMenuItems="fileActionMenuItems" />

  <!-- Skeleton: datatable layout -->
  <div v-if="loading && layout === 'datatable'" class="skeleton-content">
    <div class="skeleton-table">
      <div class="skeleton-table-header">
        <PrimeSkeleton width="2rem" height="1rem" />
        <PrimeSkeleton width="40%" height="1rem" />
        <PrimeSkeleton width="15%" height="1rem" />
        <PrimeSkeleton width="15%" height="1rem" />
        <PrimeSkeleton width="10%" height="1rem" />
      </div>
      <div v-for="n in 8" :key="n" class="skeleton-table-row">
        <PrimeSkeleton width="1.25rem" height="1.25rem" shape="circle" />
        <div class="skeleton-file-info">
          <PrimeSkeleton width="2rem" height="2rem" borderRadius="0.25rem" />
          <PrimeSkeleton :width="40 + ((n * 7) % 30) + '%'" height="1rem" />
        </div>
        <PrimeSkeleton width="6rem" height="0.875rem" />
        <PrimeSkeleton width="5rem" height="0.875rem" />
        <PrimeSkeleton width="3rem" height="0.875rem" />
      </div>
    </div>
  </div>

  <!-- Skeleton: list/compact layout -->
  <div v-else-if="loading && layout === 'list'" class="skeleton-content">
    <div class="skeleton-list">
      <div v-for="n in 10" :key="n" class="skeleton-list-row">
        <PrimeSkeleton width="1.25rem" height="1.25rem" shape="circle" />
        <PrimeSkeleton width="1.5rem" height="1.5rem" borderRadius="0.25rem" />
        <PrimeSkeleton :width="30 + ((n * 11) % 40) + '%'" height="1rem" />
        <PrimeSkeleton
          width="2rem"
          height="1.5rem"
          class="skeleton-list-action"
        />
      </div>
    </div>
  </div>

  <!-- Skeleton: grid layout -->
  <div v-else-if="loading" class="skeleton-content">
    <div class="skeleton-grid">
      <div v-for="n in 8" :key="n" class="skeleton-grid-card">
        <PrimeSkeleton width="100%" height="8rem" />
        <div class="skeleton-grid-card-footer">
          <PrimeSkeleton
            width="1.5rem"
            height="1.5rem"
            borderRadius="0.25rem"
          />
          <PrimeSkeleton :width="50 + ((n * 13) % 30) + '%'" height="1rem" />
        </div>
      </div>
    </div>
  </div>

  <!-- Ghost panel — above content, regardless of layout -->
  <div v-if="!loading && ghostNodes.length > 0" class="ghost-panel">
    <button
      class="ghost-panel-header"
      @click="ghostPanelOpen = !ghostPanelOpen"
    >
      <span class="ghost-panel-title">
        <i class="ri-loader-4-line ghost-spinner" />
        <span>{{ $t("ghost.panelTitle", { count: ghostNodes.length }) }}</span>
      </span>
      <i
        :class="ghostPanelOpen ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'"
      />
    </button>
    <div v-if="ghostPanelOpen" class="ghost-panel-body">
      <GhostNodeRow
        v-for="ghost in ghostNodes"
        :key="'ghost-' + (ghost.nodeId || ghost._originalFileName)"
        :node="ghost"
      />
    </div>
  </div>

  <div
    v-if="!loading && layout === 'datatable'"
    role="region"
    :aria-label="$t('fileList')"
  >
    <ContentTableView
      :files="files"
      :filters="filters"
      :current-node="currentNode"
      :selected-nodes="selectedNodes"
      :is-preview-panel-visible="isPreviewPanelVisible"
      :is-small-screen="isSmallScreen"
      :is-in-user-home="isInUserHome"
      :total-record-nodes="totalRecordNodes"
      :datatable-column-config="datatableColumnConfig"
      :inline-actions="effectiveDatatableInlineActions"
      :disable-inline-actions="selectedNodes.length > 0"
      :sort-field="sortField"
      :sort-order="sortOrder"
      :active-filters="activeFilters"
      :filter-options="filterOptions"
      @update:selected-nodes="updateSelectedNodes"
      @click-icon="clickIcon"
      @open-preview-panel="openPreviewPanel"
      @toggle-file-action-menu="setMenuActionAndOpenIt"
      @navigate-to="navigateTo"
      @sort-change="$emit('sort-change', $event)"
      @update:datatable-column-config="
        $emit('update:datatableColumnConfig', $event)
      "
      @filter-change="$emit('filter-change', $event)"
      @clear-all-filters="$emit('clear-all-filters')"
      @open-members="openMembers"
    />
  </div>
  <div
    v-else-if="!loading && layout === 'list'"
    role="region"
    :aria-label="$t('fileList')"
  >
    <ContentCompactTableView
      :files="files"
      :filters="filters"
      :current-node="currentNode"
      :selected-nodes="selectedNodes"
      :is-preview-panel-visible="isPreviewPanelVisible"
      :is-small-screen="isSmallScreen"
      :datatable-column-config="datatableColumnConfig"
      :inline-actions="inlineActions"
      :disable-inline-actions="selectedNodes.length > 0"
      :sort-field="sortField"
      :sort-order="sortOrder"
      :active-filters="activeFilters"
      :filter-options="filterOptions"
      @update:selected-nodes="updateSelectedNodes"
      @click-icon="clickIcon"
      @navigate-to="navigateTo"
      @toggle-file-action-menu="setMenuActionAndOpenIt"
      @sort-change="$emit('sort-change', $event)"
      @update:datatable-column-config="
        $emit('update:datatableColumnConfig', $event)
      "
      @filter-change="$emit('filter-change', $event)"
      @clear-all-filters="$emit('clear-all-filters')"
      @open-members="openMembers"
    />
  </div>
  <div v-else-if="!loading" role="region" :aria-label="$t('fileList')">
    <PrimeDataView
      v-if="currentNode"
      :value="files"
      layout="grid"
      :dt="{ borderColor: 'transparent' }"
    >
      <template #grid="slotProps">
        <ContentGridView
          :slot-props="slotProps"
          :selected-nodes="selectedNodes"
          :is-preview-panel-visible="isPreviewPanelVisible"
          :is-small-screen="isSmallScreen"
          :is-in-user-home="isInUserHome"
          @update:selected-nodes="updateSelectedNodes"
          @click-icon="clickIcon"
          @open-preview-panel="openPreviewPanel"
          @open-sharelink="openShareLink"
          @toggle-favorite="toggleFavorite"
          @toggle-file-action-menu="setMenuActionAndOpenIt"
          @open-in-new-tab="openInNewTab"
          @node-clicked="nodeClicked"
          @open-members="openMembers"
        >
          <template
            v-if="$slots['grid-card-extra']"
            #grid-card-extra="{ item }"
          >
            <slot name="grid-card-extra" :item="item" />
          </template>
        </ContentGridView>
      </template>
    </PrimeDataView>
  </div>
  <div class="pagination-bar">
    <PrimeButton
      type="button"
      icon="ri-refresh-line"
      :aria-label="$t('reload')"
      text
      @click="$emit('reload')"
    />
    <PaginatorComponent
      v-if="files && files.length > 0"
      :displayed-elements="files"
      :total-records="totalRecordNodes"
      @change-page="updateSkipCount"
      @change-element-per-page="reloadData"
    />
  </div>
  <CreateWorkflowPopup v-if="popup === 'WORKFLOW'" @close="closePopup" />
  <CopyOrMovePopup
    v-if="popup === 'COPYORMOVE'"
    :show-user-home="showUserHome"
    :action-type="actionType"
    :current-node="currentNode"
    @close="closePopup"
  />
  <ImportNewVersionPopup
    v-if="popup === 'NEWVERSION'"
    @close="closePopup"
    @message="emitMessage"
  />
  <ShareLinkPopup
    v-if="popup === 'SHARELINK'"
    @close="closePopup"
    @reload="$emit('reload')"
  />
  <MemberListPopup
    v-if="popup === 'MEMBERS'"
    :is-site="false"
    :selected-node="memberListTargetNode"
    @close="closePopup"
    @reload="$emit('reload')"
  />
  <SendMailPopup v-if="popup === 'SENDMAIL'" @close="closePopup" />
</template>

<script>
import { FilterMatchMode } from "@primevue/core/api";
import { useSelectionStore } from "@/stores/selection.js";
import { useNavigationStore } from "@/stores/navigation.js";
import alfrescoNodeService from "@/services/AlfrescoNodeService.js";
import favoriteService from "@/services/FavoriteService.js";
import ContentGridView from "./ContentGridView.vue";
import ContentCompactTableView from "./ContentCompactTableView.vue";
import ContentTableView from "./ContentTableView.vue";
import CopyOrMovePopup from "../popup/CopyOrMovePopup.vue";
import CreateWorkflowPopup from "../popup/CreateWorkflowPopup.vue";
import ImportNewVersionPopup from "../popup/ImportNewVersionPopup.vue";
import MemberListPopup from "../popup/MemberListPopup.vue";
import PaginatorComponent from "../pagination/PaginatorComponent.vue";
import SendMailPopup from "../popup/SendMailPopup.vue";
import ShareLinkPopup from "../popup/ShareLinkPopup.vue";
import Button from "primevue/button";
import DataView from "primevue/dataview";
import Skeleton from "primevue/skeleton";
import ActionMenu from "../menu/ActionMenu.vue";
import GhostNodeRow from "./GhostNodeRow.vue";

export default {
  name: "DynamicContentView",
  components: {
    ActionMenu,
    ContentCompactTableView,
    ContentGridView,
    ContentTableView,
    CopyOrMovePopup,
    CreateWorkflowPopup,
    GhostNodeRow,
    ImportNewVersionPopup,
    MemberListPopup,
    PaginatorComponent,
    SendMailPopup,
    ShareLinkPopup,
    PrimeButton: Button,
    PrimeDataView: DataView,
    PrimeSkeleton: Skeleton,
  },
  inject: [
    "actionsService",
    "collaboraService",
    "isCurrentNodeDisplayed",
    "setIsPopupOpen",
    "toggleFileActionMenu",
    "getFileActionMenuItems",
  ],
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    files: {
      type: Array,
      default: () => [],
    },
    canUpload: {
      type: Boolean,
      default: false,
    },
    layout: {
      type: String,
      default: null,
    },
    currentNode: {
      type: Object,
      default: () => ({}),
    },
    isPreviewPanelVisible: {
      type: Boolean,
      default: false,
    },
    isSmallScreen: {
      type: Boolean,
      default: false,
    },
    isInUserHome: {
      type: Boolean,
      default: false,
    },
    totalRecordNodes: {
      type: Number,
      default: 0,
    },
    datatableColumnConfig: {
      type: Array,
      default: () => [],
    },
    /**
     * Inline action buttons for the compact table (list layout).
     * Each item: { icon, tooltip, severity?, handler(event, node), visible?(node) }
     */
    inlineActions: {
      type: Array,
      default: () => [],
    },
    columnToggle: {
      type: Boolean,
      default: false,
    },
    sortField: {
      type: String,
      default: null,
    },
    sortOrder: {
      type: Number,
      default: null,
    },
    activeFilters: {
      type: Object,
      default: () => ({}),
    },
    filterOptions: {
      type: Object,
      default: () => ({}),
    },
    ghostNodes: {
      type: Array,
      default: () => [],
    },
  },
  emits: [
    "message",
    "open-modify",
    "open-rename",
    "open-preview-panel",
    "reload",
    "update-skipcount",
    "navigate-to",
    "update:datatableColumnConfig",
    "sort-change",
    "filter-change",
    "clear-all-filters",
  ],
  computed: {
    hasExtraSlots() {
      return (
        !!this.$slots["list-item-extra"] || !!this.$slots["grid-card-extra"]
      );
    },
    effectiveDatatableInlineActions() {
      if (this.inlineActions.length > 0) {
        return this.inlineActions;
      }
      return [
        {
          icon: "ri-share-line",
          tooltip: this.$t("share.shareLinkSettings"),
          handler: (_event, node) => this.openShareLink(node),
          visible: (node) =>
            node.isFile && !this.isPreviewPanelVisible && !this.isSmallScreen,
        },
        {
          icon: (node) => (node.isFavorite ? "ri-star-fill" : "ri-star-line"),
          tooltip: (node) =>
            node.isFavorite
              ? this.$t("removeFavorite")
              : this.$t("addFavorite"),
          handler: (_event, node) => this.toggleFavorite(node),
          visible: () =>
            !this.isPreviewPanelVisible &&
            !this.isSmallScreen &&
            !this.isInUserHome,
        },
      ];
    },
  },
  watch: {
    ghostNodes: {
      immediate: true,
      handler(nodes) {
        this.ghostPanelOpen = nodes.length <= 3;
      },
    },
  },
  data() {
    return {
      filters: {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      ghostPanelOpen: true,
      fileActionMenuItems: [],
      popup: null,
      memberListTargetNode: null,
      selectedNodes: [],
      modifyPopupTitle: "",
      actionType: null,
      showUserHome: false,
    };
  },
  created() {
    this.initFilters();
    this.selectionStore = useSelectionStore();
    this.selectedNodes = this.selectionStore.nodes;
    this.selectionStore.$subscribe((mutation, state) => {
      this.selectedNodes = state.nodes;
    });
  },
  methods: {
    setMenuActionAndOpenIt(event, item) {
      this.fileActionMenuItems = this.getFileActionMenuItems(item);
      this.toggleFileActionMenu(event);
    },
    getMenuActionRef() {
      return this.$refs.fileMenuAction;
    },
    updateSkipCount(skipCount) {
      this.$emit("update-skipcount", skipCount);
    },
    reloadData() {
      this.$emit("reload");
    },
    updateSelectedNodes(values) {
      this.selectionStore.selectNodes(values);
    },
    closePopup(reload) {
      this.setIsPopupOpen(false);
      if (this.popup === "MODIFYNODE") {
        this.$emit("reload");
      }
      this.popup = null;
      this.memberListTargetNode = null;
      if (reload) {
        this.$emit("reload");
      }
    },
    emitMessage(message) {
      this.$emit("message", message);
    },
    initFilters() {
      this.filters = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      };
    },
    openInNewTab(item) {
      let route;
      if (item.isFolder) {
        route = this.$router.resolve(
          this.actionsService.getRouteForNavigationInNewTab(
            useNavigationStore().workspace.id,
            item,
          ),
        );
      } else {
        route = this.$router.resolve(
          this.actionsService.getRouteForOpenInNewTab(item),
        );
      }
      window.open(route.href, "_blank_" + item.id);
    },
    deleteItem(item) {
      alfrescoNodeService
        .deleteNode(item.id, item.isFile ? "fichier" : "dossier")
        .then(() => {
          this.$emit("reload");
          this.$toast.add({
            severity: "success",
            summary: this.$t("success"),
            detail: this.$t("node.successRemoveElement"),
            life: 3000,
          });
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: "Error",
            detail: err.message,
            life: 3000,
          });
        });
    },
    toggleDownloadFile(item) {
      this.actionsService.toggleDownloadFile(item);
    },
    gotoConsultationPdf(item) {
      this.$router.push({
        name: "consultation-pdf",
        params: {
          id: item.id,
        },
      });
    },
    gotoEditCollabora(item) {
      let routeData = this.$router.resolve({
        name: "edit-collabora",
        params: { action: "edit", id: item.id },
      });
      window.open(routeData.href, "_blank");
    },
    openCopy(item) {
      if (item) {
        this.actionType = "copy";
        this.showUserHome = true;
        this.setIsPopupOpen(true);
        this.selectionStore.selectSingleNode(item);
        this.popup = "COPYORMOVE";
      }
    },
    openMove(item) {
      if (item) {
        this.actionType = "move";
        this.showUserHome = true;
        this.setIsPopupOpen(true);
        this.selectionStore.selectSingleNode(item);
        this.popup = "COPYORMOVE";
      }
    },
    openNewVersion(item) {
      if (item) {
        this.setIsPopupOpen(true);
        this.selectionStore.selectSingleNode(item);
        this.popup = "NEWVERSION";
      }
    },
    openShareLink(item) {
      if (item) {
        this.setIsPopupOpen(true);
        this.selectionStore.selectSingleNode(item);
        this.popup = "SHARELINK";
      }
    },
    openCreateWorkflow(item) {
      if (item) {
        this.setIsPopupOpen(true);
        this.selectionStore.selectSingleNode(item);
        this.popup = "WORKFLOW";
      }
    },
    copyInternLink(item) {
      if (item) {
        const link = this.actionsService.getNodeInternLink(item);
        navigator.clipboard
          .writeText(link)
          .then(() => {
            this.$toast.add({
              severity: "success",
              summary: this.$t("share.copyLinkToastSummary"),
              detail: this.$t("share.copyLinkToastDetail"),
              life: 3000,
            });
          })
          .catch(() => {
            this.$toast.add({
              severity: "error",
              summary: this.$t("share.copyLinkErrorSummary"),
              detail: this.$t("share.copyLinkErrorDetail"),
              life: 3000,
            });
          });
      }
    },
    openSendMail(item) {
      if (item && item.isFile) {
        this.setIsPopupOpen(true);
        this.selectionStore.selectSingleNode(item);
        this.popup = "SENDMAIL";
      }
    },
    openMembers(item) {
      if (item) {
        this.setIsPopupOpen(true);
        this.memberListTargetNode = item;
        this.popup = "MEMBERS";
      }
    },
    toggleFavorite(file) {
      favoriteService
        .changeSingleFavorite(file)
        .then((favorite) => {
          this.$toast.add({
            severity: "success",
            summary: this.$t("menu.favoris"),
            detail: favorite.isFavorite
              ? this.$t("node.addElementFavorite", {
                  name: favorite.name,
                })
              : this.$t("node.removeElementFavorite", {
                  name: favorite.name,
                }),
            life: 3000,
          });
        })
        .catch((err) => {
          this.$toast.add({
            severity: "error",
            summary: this.$t("node.favoriteError"),
            detail: err.message,
            life: 3000,
          });
        });
    },
    selectNode(node) {
      this.selectedNodes = node;
      this.selectionStore.selectSingleNode(node);
    },
    navigateTo(node) {
      this.$emit("navigate-to", node);
    },
    nodeClicked(node) {
      if (node.isFolder || this.isSmallScreen) {
        this.navigateTo(node);
      }
    },
    openPreviewPanel(node) {
      if (node.isFile) {
        this.$emit("open-preview-panel", node);
      }
    },
    clickIcon(node) {
      if (node.isFolder || this.isSmallScreen) {
        this.navigateTo(node);
      } else if (node.isFile) {
        this.openPreviewPanel(node);
      }
    },
  },
};
</script>
<style lang="scss" scoped>
.pagination-bar {
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
}

.list-header {
  display: grid;
  grid-template-columns: 2.5rem 1fr 7rem 9rem;
  align-items: center;
  gap: 0.75rem;
  padding: var(--p-dataview-padding);
  font-weight: 600;
  font-size: 0.875rem;
  color: var(--p-text-muted-color);

  &.has-extra-column {
    grid-template-columns: 2.5rem 1fr 8rem 7rem 9rem;
  }

  &.compact-mode {
    grid-template-columns: 2.5rem 1fr 3rem;

    &.has-extra-column {
      grid-template-columns: 2.5rem 1fr 3rem;
    }
  }
}

.col-checkbox {
  display: flex;
  align-items: center;
  justify-content: center;
}

.col-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.col-date {
  text-align: center;
}

.col-actions {
  text-align: right;
}

.font-bold {
  font-weight: 600;
}

/* Skeleton styles */
.skeleton-table {
  border-radius: 0.5rem;
  overflow: hidden;
}

.skeleton-table-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1rem;
  background: var(--p-surface-ground);
  border-bottom: 1px solid var(--p-surface-border);
}

.skeleton-table-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid var(--p-surface-border);

  &:last-child {
    border-bottom: none;
  }
}

.skeleton-file-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
}

.skeleton-list {
  display: flex;
  flex-direction: column;
}

.skeleton-list-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 1rem;
  border-bottom: 1px solid var(--p-surface-border);

  &:last-child {
    border-bottom: none;
  }
}

.skeleton-list-action {
  margin-left: auto;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
  padding: 1rem;
}

.skeleton-grid-card {
  border: 1px solid var(--p-surface-border);
  border-radius: 0.5rem;
  overflow: hidden;
}

.skeleton-grid-card-footer {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
}

.ghost-panel {
  border: 1px solid var(--p-surface-border);
  border-radius: 0.5rem;
  background: var(--p-content-hover-background);
  margin-bottom: 0.75rem;
  overflow: hidden;
}

.ghost-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.625rem 1rem;
  background: var(--p-content-hover-background);
  border: none;
  border-bottom: 1px solid var(--p-surface-border);
  cursor: pointer;
  font: inherit;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.ghost-panel-title {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
}

.ghost-spinner {
  animation: ghost-spin 1s linear infinite;
}

@keyframes ghost-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
